import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '@shared/ui';
import { Sidebar, useSidebarConfig } from '../sidebar';
import type { SidebarItem } from '../sidebar';
import { PageHeader } from './page-header';
import { MobileTopBar } from './mobile-top-bar';
import { MobileNavDrawer } from './mobile-nav-drawer';

const NO_SIDEBAR_ROUTES = [
  '/auth',
  '/login',
  '/register',
  '/404',
  '/invoice',
  '/forgot-password',
  '/reset-password',
];

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/balance': 'balance.title',
  '/payments': 'sidebar.payments',
  '/projects': 'projects.title',
  '/projects/add': 'projects.form.pageTitle',
  '/withdrawals': 'sidebar.withdrawals',
  '/referrals': 'referral.title',
  '/service-accounting': 'sidebar.serviceAccounting',
  '/wallets': 'sidebar.wallets',
  '/workspace': 'sidebar.workspace',
  '/audit-log': 'sidebar.auditLog',
  '/security': 'security.section',
  '/compliance': 'sidebar.compliance',
  '/aml-settings': 'sidebar.amlSettings',
  '/aml-billing': 'sidebar.amlBilling',
};

interface LayoutWrapperProps {
  children: React.ReactNode;
}

export function LayoutWrapper({ children }: LayoutWrapperProps): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('cabinet');
  const sidebarItems = useSidebarConfig();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const shouldShowSidebar = !NO_SIDEBAR_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // Drawer lives below `lg`; closing it on resize releases the Radix scroll lock.
  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setIsMobileNavOpen(false);
    };

    desktopQuery.addEventListener('change', handleChange);
    return () => desktopQuery.removeEventListener('change', handleChange);
  }, []);

  const handleSidebarItemClick = (item: SidebarItem) => {
    navigate(item.href);
  };

  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname];
  const pageTitle = pageTitleKey ? t(pageTitleKey) : '';

  if (!shouldShowSidebar) {
    return <>{children}</>;
  }

  return (
    <div
      data-cabinet=""
      className="flex min-h-[100dvh] bg-background text-foreground lg:h-[100dvh] lg:max-h-[100dvh] lg:overflow-hidden"
    >
      <Sidebar
        items={sidebarItems}
        onItemClick={handleSidebarItemClick}
        className="hidden h-full shrink-0 lg:flex"
      />

      {/* overflow-x-clip, not hidden: hidden forces overflow-y:auto and brings back a second scrollbar */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip lg:overflow-y-hidden">
        <MobileTopBar onMenuOpen={() => setIsMobileNavOpen(true)} className="shrink-0 lg:hidden" />
        <PageHeader title={pageTitle} className="hidden shrink-0 lg:block" />

        <main className="min-h-0 flex-1 py-[var(--layout-page-padding-y-mobile)] sm:py-[var(--layout-page-padding-y-tablet)] lg:overflow-y-auto lg:overscroll-y-contain lg:px-0 lg:py-0 lg:pt-[var(--layout-page-padding-top,32px)]">
          <PageContainer>{children}</PageContainer>
        </main>
      </div>

      <MobileNavDrawer
        open={isMobileNavOpen}
        onOpenChange={setIsMobileNavOpen}
        items={sidebarItems}
        onItemClick={handleSidebarItemClick}
      />
    </div>
  );
}
